import { useEffect, useState } from 'react'
import { Plus, RefreshCw } from 'lucide-react'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { Modal, Field } from '@/shared/ui/Modal'
import { useCan } from '@/shared/auth/session'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import type { KpiResponse, ProgramResponse } from '@/shared/api/types'
import { createKpi, listKpis, recomputeKpis, updateKpi } from '../api'
import { Meter, Unavailable } from '../parts'
import { KPI_DIRECTION_ORDER, KpiSourceBadge, kpiDirectionLabel } from '../vocab'

/**
 * KPIs, and the honesty column that makes them worth showing.
 *
 * `source` is `NOT NULL` on the server and is rendered on every row. A `MANUAL`
 * KPI is a number somebody typed and its chip says "Reported"; a `DERIVED_*` KPI
 * is computed from this feature's own tables and its current value has no input
 * at all — a PATCH that tries is a 409 naming the source.
 *
 * PollSphere let a user type a "current value" into a KPI and then charted it as
 * achievement. That is the difference between a dashboard and a wish.
 */
export function KpisPanel({ program }: { program: ProgramResponse }) {
  const canUpdate = useCan('program:update')
  const [rows, setRows] = useState<KpiResponse[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<KpiResponse | 'new' | null>(null)
  const [busy, setBusy] = useState(false)

  const load = () => listKpis(program.id).then(setRows).catch((e) => setError(explain(e)))

  useEffect(() => {
    let alive = true
    listKpis(program.id)
      .then((r) => { if (alive) setRows(r) })
      .catch((e) => { if (alive) setError(explain(e)) })
    return () => { alive = false }
  }, [program.id])

  const recompute = async () => {
    setBusy(true)
    try { setRows(await recomputeKpis(program.id)) }
    catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  if (!rows) return <LoadingBlock label="Loading KPIs…" />

  return (
    <div className="space-y-3 px-1">
      <ErrorStrip message={error} />
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-erp-sm font-medium">{rows.length} indicator{rows.length === 1 ? '' : 's'}</h4>
        {canUpdate && (
          <div className="flex gap-2">
            <button type="button" className="erp-btn h-7 text-erp-xs" onClick={recompute} disabled={busy}>
              <RefreshCw className="h-3.5 w-3.5" /> Recompute derived
            </button>
            <button type="button" className="erp-btn h-7 text-erp-xs" onClick={() => setEditing('new')}>
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="text-erp-xs text-muted-foreground">No indicators defined.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((k) => {
            const current = k.currentValue === null || k.currentValue === undefined
              ? null : Number(k.currentValue)
            const target = k.targetValue === null || k.targetValue === undefined
              ? null : Number(k.targetValue)
            return (
              <li key={k.id} className="rounded-md border border-border p-2 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-erp-sm font-medium">{k.name}</span>
                  <KpiSourceBadge code={k.sourceCode} />
                  {canUpdate && k.editable && (
                    <button type="button" className="erp-btn-ghost h-7 text-erp-xs ml-auto"
                      onClick={() => setEditing(k)}>Edit</button>
                  )}
                  {canUpdate && !k.editable && (
                    <span className="ml-auto text-erp-xs text-muted-foreground">
                      computed — not editable
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-baseline gap-3">
                  <span className="text-erp-lg font-semibold tabular-nums">
                    {/* A DERIVED KPI with nothing to derive from shows its note,
                        not a fabricated 0. */}
                    {current === null
                      ? <Unavailable reason={k.note ?? 'Not measured yet.'} />
                      : `${fmtNumber(current)}${k.unit ? ` ${k.unit}` : ''}`}
                  </span>
                  {target !== null && (
                    <span className="text-erp-xs text-muted-foreground">
                      target {fmtNumber(target)}{k.unit ? ` ${k.unit}` : ''}
                    </span>
                  )}
                  <span className="ml-auto">
                    <Meter value={current} max={target} direction={k.directionCode}
                      reason={target === null ? 'No target set.' : undefined} />
                  </span>
                </div>
                <p className="text-erp-xs text-muted-foreground">
                  {kpiDirectionLabel(k.directionCode)}
                  {k.measuredAt ? ` · measured ${fmtDateTime(k.measuredAt)}` : ''}
                </p>
              </li>
            )
          })}
        </ul>
      )}

      <Note tone="info">
        A <strong>Reported</strong> indicator is a number somebody typed. A{' '}
        <strong>Derived</strong> one is computed from this programme's own beneficiary,
        delivery and ledger rows, and has no input anywhere in this screen.
      </Note>

      {editing && (
        <KpiDialog
          programId={program.id}
          existing={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load() }}
        />
      )}
    </div>
  )
}

function KpiDialog({ programId, existing, onClose, onSaved }: {
  programId: string; existing?: KpiResponse; onClose: () => void; onSaved: () => void
}) {
  const [code, setCode] = useState(existing?.code ?? '')
  const [name, setName] = useState(existing?.name ?? '')
  const [unit, setUnit] = useState(existing?.unit ?? '')
  const [directionCode, setDirection] = useState(existing?.directionCode ?? 'HIGHER_IS_BETTER')
  const [targetValue, setTarget] = useState(existing?.targetValue == null ? '' : String(existing.targetValue))
  const [baselineValue, setBaseline] = useState(existing?.baselineValue == null ? '' : String(existing.baselineValue))
  const [currentValue, setCurrent] = useState(existing?.currentValue == null ? '' : String(existing.currentValue))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setBusy(true); setError(null)
    const body = {
      ...(existing ? {} : { code, sourceCode: 'MANUAL' }),
      name, unit: unit || null, directionCode,
      targetValue: targetValue === '' ? null : Number(targetValue),
      baselineValue: baselineValue === '' ? null : Number(baselineValue),
      currentValue: currentValue === '' ? null : Number(currentValue),
    }
    try {
      if (existing) await updateKpi(programId, existing.id, body)
      else await createKpi(programId, body)
      onSaved()
    } catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} title={existing ? `Edit ${existing.name}` : 'New indicator'} footer={(
      <>
        <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="erp-btn-primary" onClick={submit}
          disabled={busy || !name || (!existing && !code)}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </>
    )}>
      <div className="space-y-3">
        <ErrorStrip message={error} />
        <Note tone="info">
          An indicator added here is <strong>Reported</strong> — a number somebody types. Every
          screen labels it that way, and no screen presents it as an achievement.
        </Note>
        {!existing && (
          <Field label="Code" required>
            <input className="erp-input" value={code} maxLength={48}
              onChange={(e) => setCode(e.target.value.toUpperCase())} />
          </Field>
        )}
        <Field label="Name" required>
          <input className="erp-input" value={name} maxLength={160}
            onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Unit"><input className="erp-input" value={unit ?? ''} maxLength={32}
            onChange={(e) => setUnit(e.target.value)} /></Field>
          <Field label="Direction">
            <select className="erp-input" value={directionCode}
              onChange={(e) => setDirection(e.target.value as typeof directionCode)}>
              {KPI_DIRECTION_ORDER.map((d) => (
                <option key={d} value={d}>{kpiDirectionLabel(d)}</option>
              ))}
            </select>
          </Field>
          <Field label="Baseline"><input className="erp-input" type="number" value={baselineValue}
            onChange={(e) => setBaseline(e.target.value)} /></Field>
          <Field label="Target"><input className="erp-input" type="number" value={targetValue}
            onChange={(e) => setTarget(e.target.value)} /></Field>
        </div>
        <Field label="Current value" hint="Leave empty if it has not been measured — the screens will say so rather than showing zero.">
          <input className="erp-input" type="number" value={currentValue}
            onChange={(e) => setCurrent(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
